import type { DnaFacetRecord } from '../lib/api';
import clsx from './clsx';
import styles from './FacetChip.module.css';

// =============================================================================
// FacetChip — one banked DNA facet in the interview's "Captured so far" list
// (LANE DNA-BUILD, Phase 1). Re-skinned port of apps/web/src/pages/Dna.tsx's
// inline FacetChip fn + FACET_STYLE map (:35-41,471-489).
//
// Color mapping: the plan (docs/design/dna-interview-port-2026-07-05.md §3
// Phase 1) recommends reusing the bucket vocabulary's color-DISTINCTNESS
// discipline rather than minting a 7th palette, while flagging this as worth
// confirming with Jason rather than assuming. Following that recommendation
// as the shipped default — each facet type below borrows ONE existing
// --tl-bucket-* token (5 of the 6 buckets, chosen for the closest semantic
// echo: a win reads like "home" — the safe, positive bucket; a driver reads
// like "dream" — the aspirational pull) rather than inventing new hues. Open
// for Jason to override; nothing here blocks on that decision since it is
// purely a token substitution, not a structural choice.
// =============================================================================

const FACET_STYLE: Record<string, { label: string; colorVar: string; softVar: string }> = {
  accomplishment: {
    label: 'Win',
    colorVar: '--tl-bucket-home',
    softVar: '--tl-bucket-home-soft',
  },
  knowledge: {
    label: 'Knowledge',
    colorVar: '--tl-bucket-relocate',
    softVar: '--tl-bucket-relocate-soft',
  },
  lesson: {
    label: 'Lesson',
    colorVar: '--tl-bucket-bridge',
    softVar: '--tl-bucket-bridge-soft',
  },
  thread: {
    label: 'Thread',
    colorVar: '--tl-bucket-unsorted',
    softVar: '--tl-bucket-unsorted-soft',
  },
  driver: {
    label: 'Driver',
    colorVar: '--tl-bucket-dream',
    softVar: '--tl-bucket-dream-soft',
  },
};

const FALLBACK_STYLE = { colorVar: '--tl-ink-soft', softVar: '--tl-surf2' };

export interface FacetChipProps {
  facet: DnaFacetRecord;
}

export function FacetChip({ facet }: FacetChipProps) {
  const meta = FACET_STYLE[facet.facetType];
  const label = meta?.label ?? facet.facetType;
  const colorVar = meta?.colorVar ?? FALLBACK_STYLE.colorVar;
  const softVar = meta?.softVar ?? FALLBACK_STYLE.softVar;
  return (
    <div
      className={styles.chip}
      style={{ color: `var(${colorVar})`, background: `var(${softVar})` }}
    >
      <span className={clsx(styles.label)} style={{ color: `var(${colorVar})` }}>
        {label}
      </span>
      <span className={styles.content}>{facet.content}</span>
      {facet.isNormalToMe === 1 ? (
        <span title="The gold you undersell" className={styles.gold}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2l2.4 7.2H22l-6 4.4 2.3 7.2L12 16.4l-6.3 4.4 2.3-7.2-6-4.4h7.6z" />
          </svg>
        </span>
      ) : null}
    </div>
  );
}
